import { KeyboardEvent, useState } from 'react';

interface UseInlineEditParams<T> {
  value: T;
  isDisabled?: boolean;
  shouldConfirmOnEnter?: boolean;
  validate?: (value: T) => string | undefined;
  onConfirm: (value: T) => void;
}

export const useInlineEdit = <T>({
  value,
  isDisabled,
  shouldConfirmOnEnter = true,
  validate,
  onConfirm,
}: UseInlineEditParams<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const [editingStartValue, setEditingStartValue] = useState(value);
  const [error, setError] = useState<string>();

  const handleStartEditing = () => {
    if (isDisabled) {
      return;
    }

    setError(undefined);
    setDraftValue(value);
    setEditingStartValue(value);
    setIsEditing(true);
  };

  const handleChangeDraft = (nextValue: T) => {
    setError(undefined);
    setDraftValue(nextValue);
  };

  const handleConfirm = () => {
    const validationError = validate?.(draftValue);

    if (validationError) {
      setError(validationError);
      return;
    }

    setIsEditing(false);

    if (draftValue === editingStartValue) {
      return;
    }

    onConfirm(draftValue);
  };

  const handleCancel = () => {
    setError(undefined);
    setDraftValue(value);
    setIsEditing(false);
  };

  const handleBlur = () => {
    const isDraftInvalid = Boolean(validate?.(draftValue));

    if (isDraftInvalid) {
      handleCancel();
      return;
    }

    handleConfirm();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'Enter' && shouldConfirmOnEnter) {
      event.preventDefault();
      handleConfirm();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      handleCancel();
    }
  };

  return {
    error,
    isEditing,
    draftValue: isEditing ? draftValue : value,
    handleStartEditing,
    handleChangeDraft,
    handleConfirm,
    handleCancel,
    handleBlur,
    handleKeyDown,
  };
};
